import { distinctUntilChanged, MonoTypeOperatorFunction } from 'rxjs'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ClassOf<T> = new (...args: any[]) => T
export type WithDefinedField<T, K extends keyof T> = T & {
	[Key in keyof T]-?: Key extends K ? T[Key] : never
}
export type WithNotNullField<T, K extends keyof T> = T & { [P in K]: Exclude<T[P], null> }
export type WithValidField<T, K extends keyof T> = T & { [P in K]-?: Exclude<T[P], null> }

export function hasNoNullProperties<T extends object>(value: T): value is WithNotNullField<T, keyof T>
export function hasNoNullProperties<T extends object, K extends keyof T>(
	value: T,
	keys: K[],
): value is WithNotNullField<T, K>
export function hasNoNullProperties<T extends object, K extends keyof T>(
	value: T,
	keys?: K[],
): value is WithNotNullField<T, K> {
	keys = keys ?? (Object.keys(value) as K[])
	return keys.every((key) => value[key] !== null)
}

export function hasValidProperties<T extends object>(value: T): value is WithValidField<T, keyof T>
export function hasValidProperties<T extends object, K extends keyof T>(
	value: T,
	keys: K[],
): value is WithValidField<T, K>
export function hasValidProperties<T extends object, K extends keyof T>(
	value: T,
	keys?: K[],
): value is WithValidField<T, K> {
	keys = keys ?? (Object.keys(value) as K[])
	return keys.every((key) => value[key] !== null && value[key] !== undefined)
}

export function isDefined<T>(value: T): value is WithDefinedField<T, keyof T> {
	return value !== undefined
}

export function distinctUntilArrayChanged<T>(
	compare: (a: T, b: T) => number = (a, b) => (a > b ? 1 : a < b ? -1 : 0),
): MonoTypeOperatorFunction<T[]> {
	return distinctUntilChanged((a, b) => {
		// Sort new array to make sure that order of elements doesn't matter
		const sortedA = [...a].sort(compare)
		const sortedB = [...b].sort(compare)
		return (
			sortedA.length === sortedB.length && sortedA.every((value, index) => compare(value, sortedB[index]) === 0)
		)
	})
}

export type DeepPartial<T> = {
	[K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K]
}

export const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))
